/**
 * Price — render a server-computed ruble amount in the display face.
 *
 * IMPORTANT: this is presentation only. The amount must already be computed by the
 * server (the frontend never derives prices/totals/discounts). It accepts the value
 * as a string (DRF ``Decimal`` serializes to string) or number and formats it for
 * the ``ru-RU`` locale.
 */
import { cn } from "../utils/cn";
import styles from "./Price.module.css";

export interface PriceProps {
  /** Server-provided amount in rubles (string from DRF Decimal, or number). */
  readonly amount: string | number;
  /** Optional original amount to show struck-through (e.g. before discount). */
  readonly original?: string | number;
  readonly size?: "sm" | "md" | "lg";
  /** Tint the value gold for emphasis. Defaults to ``false``. */
  readonly accent?: boolean;
  readonly className?: string;
}

const formatter = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  maximumFractionDigits: 0,
});

function format(value: string | number): string {
  const numeric = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(numeric)) {
    return String(value);
  }
  return formatter.format(numeric);
}

export function Price({
  amount,
  original,
  size = "md",
  accent = false,
  className,
}: PriceProps): JSX.Element {
  return (
    <span className={cn(styles.price, styles[size], className)}>
      {original !== undefined ? (
        <span className={styles.original}>{format(original)}</span>
      ) : null}
      <span className={cn(styles.value, accent && styles.accent)}>
        {format(amount)}
      </span>
    </span>
  );
}
